import { useNavigate } from 'react-router';
import { ArrowUp, ArrowDown, Image, MapPin, MessageCircle, TriangleAlert } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { CategoryIcon } from '@/components/common/CategoryIcon';
import { ExpenseShareLabel } from '@/components/expenses/ExpenseShareLabel';
import { format } from 'date-fns';
import type { ExpenseWithGroup, AllExpensesFilters } from '@/api/expenses';
import { useTranslation } from 'react-i18next';

type SortBy = NonNullable<AllExpensesFilters['sortBy']>;
type SortOrder = NonNullable<AllExpensesFilters['sortOrder']>;

interface ExpenseTableProps {
  expenses: ExpenseWithGroup[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleAll: () => void;
  sortBy: SortBy;
  sortOrder: SortOrder;
  onSort: (column: SortBy) => void;
  commentCounts?: Record<string, number>;
}

function SortIndicator({ column, active, order }: { column: SortBy; active: SortBy; order: SortOrder }) {
  if (column !== active) return <ArrowUp className="h-3 w-3 opacity-0 group-hover:opacity-30" />;
  return order === 'asc'
    ? <ArrowUp className="h-3 w-3 text-brand-600" />
    : <ArrowDown className="h-3 w-3 text-brand-600" />;
}

export function ExpenseTable({
  expenses,
  selectedIds,
  onToggleSelect,
  onToggleAll,
  sortBy,
  sortOrder,
  onSort,
  commentCounts,
}: ExpenseTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const allSelected = expenses.length > 0 && expenses.every(e => selectedIds.has(e.id));

  return (
    <div className="overflow-x-auto rounded-xl bg-surface-primary shadow-sm ring-1 ring-border-primary">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border-primary bg-surface-secondary text-left text-xs font-medium uppercase tracking-wider text-text-quaternary">
            <th className="w-10 px-4 py-3">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleAll}
                className="h-4 w-4 rounded border-border-secondary text-brand-600 focus:ring-brand-500"
              />
            </th>
            <th
              className="group cursor-pointer px-4 py-3"
              onClick={() => onSort('description')}
            >
              <span className="flex items-center gap-1">
                {t('expenses:form.description.label')}
                <SortIndicator column="description" active={sortBy} order={sortOrder} />
              </span>
            </th>
            <th className="whitespace-nowrap px-4 py-3">{t('expenses:detail.group')}</th>
            <th className="whitespace-nowrap px-4 py-3">{t('expenses:detail.paidBy')}</th>
            <th className="whitespace-nowrap px-4 py-3">{t('expenses:form.category.label')}</th>
            <th
              className="group cursor-pointer whitespace-nowrap px-4 py-3 text-right"
              onClick={() => onSort('amount')}
            >
              <span className="flex items-center justify-end gap-1">
                {t('settlements:section.amount')}
                <SortIndicator column="amount" active={sortBy} order={sortOrder} />
              </span>
            </th>
            <th
              className="group cursor-pointer whitespace-nowrap px-4 py-3 text-right"
              onClick={() => onSort('date')}
            >
              <span className="flex items-center justify-end gap-1">
                {t('settlements:view.date')}
                <SortIndicator column="date" active={sortBy} order={sortOrder} />
              </span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-secondary">
          {expenses.map(expense => {
            const isSelected = selectedIds.has(expense.id);

            const isPayer = expense.paid_by === user?.id;

            return (
              <tr
                key={expense.id}
                onClick={() => navigate(`/expenses/${expense.id}`)}
                className={`cursor-pointer transition-colors hover:bg-surface-hover ${
                  isSelected ? 'bg-brand-50/50 dark:bg-brand-900/10' : ''
                }`}
              >
                <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(expense.id)}
                    className="h-4 w-4 rounded border-border-secondary text-brand-600 focus:ring-brand-500"
                  />
                </td>
                <td className="max-w-xs px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-text-primary line-clamp-1">{expense.description}</span>
                    {expense.has_open_dispute && (
                      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-red-500/15 px-1.5 py-0.5 text-red-600 dark:text-red-400" title={t('web:common.hasOpenDispute')}>
                        <TriangleAlert className="h-3 w-3" />
                        <span className="text-[10px] font-medium">{t('expenses:swipeable.dispute')}</span>
                      </span>
                    )}
                    {commentCounts?.[expense.id] && (
                      <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-blue-600 dark:text-blue-400" title={t('web:common.comments', { count: commentCounts[expense.id] })}>
                        <MessageCircle className="h-3 w-3" />
                        <span className="text-[10px] font-medium">{commentCounts[expense.id]}</span>
                      </span>
                    )}
                    {expense.receipt_url && (
                      <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-500/15 p-1 text-emerald-600 dark:text-emerald-400" title={t('web:common.hasReceipt')}>
                        <Image className="h-3 w-3" />
                      </span>
                    )}
                    {(expense.location_name || expense.latitude) && (
                      <span className="inline-flex shrink-0 items-center rounded-full bg-amber-500/15 p-1 text-amber-600 dark:text-amber-400" title={expense.location_name ?? t('web:common.hasLocation')}>
                        <MapPin className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  {expense.group && (
                    <span className="flex items-center gap-1.5 text-sm text-text-secondary">
                      <span
                        className="inline-block h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: expense.group.color ?? '#6366F1' }}
                      />
                      <span className="truncate max-w-[120px]">{expense.group.name}</span>
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <span className="text-sm text-text-secondary">
                    {expense.paid_by_user?.display_name ?? t('web:common.unknownUser')}
                    {isPayer && <span className="ml-1 text-text-quaternary">{t('web:common.youSuffix')}</span>}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <CategoryIcon icon={expense.category?.icon} color={expense.category?.color} size="sm" />
                    <span className="text-sm text-text-secondary">{expense.category?.name ?? t('expenses:systemCategory.Other')}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <AmountDisplay
                    amountMinor={Number(expense.amount_minor_src)}
                    currency={expense.currency_code_src}
                    className="text-sm font-medium text-text-primary"
                  />
                  <ExpenseShareLabel expense={expense} userId={user?.id} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <span className="text-sm text-text-secondary">
                    {format(new Date(expense.expense_date), 'MMM d, yyyy')}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
